import { DateTime } from 'luxon';
import { OnboardingSteps } from '../../shared/types/user-setting';
import {
  PlanType,
  SubscriptionPlans,
} from '../../shared/utils/subscription-plans';
import SubscriptionStatus from '../settings/components/billing-subscription/enums/subscription-status';
import { FeatureCode } from '../../shared/components/interest-form';
import {
  InterestFormPayloadItem,
  UserFeatureEngagementStatus,
} from '../../shared/components/interest-form/interest-form-types';

export type UpgradeOnboardingStepPayload = {
  onboardingStep: OnboardingSteps;
};

export type SubscriptionDetails = {
  planId: number;
  planName: string;
  planAmount: string;
  planMode: number;
  slots: number;
  emailAccountQuota: number;
  userQuota: number;
  startAt: string;
  nextBillingAt: string;
  subscriptionId(pin): string;
  customerId: string;
  paddleSubscriptionId: string;
  paddleCustomerId: string;
  planType: PlanType;
  planCode: SubscriptionPlans;
  planTransitionDetails: {
    current: number;
    from: number;
  };
  status: SubscriptionStatus;
  pauseEndsAt?: string;
  pausedAt?: string;
};

export type PaymentAction = {
  application: 'sequence' | 'lead-finder' | 'inbox-analyzer';
  paymentActionRequired: boolean;
  paymentActionUrl: string;
  paymentActionType: string;
};

export enum NotificationType {
  SequencePaused = 'sequence-paused',
  SequencePausedBounce = 'sequence-paused-bounce',
  EmailAccountDisconnected = 'email-account-disconnected',
  SequenceBulkProspectClient = 'sequence-prospect-bulk-client',
  SequenceProspectExportForClient = 'sequence-prospect-export-client',
  UniboxConversationForClient = 'unibox-conversation-client',
  EmailAccountBulkConnectClient = 'email-account-bulk-connect-client',
  SequenceReportClient = 'sequence-report-client',
  ProspectVerifyReport = 'prospect-verify-report',
  WhitelabelDomainSet = 'whitelabel-domain-set',
  SequenceProspectImport = 'sequence-prospect-import',
  DnsImport = 'dnc-import',
  DnsExport = 'dnc-export',
  SequenceProspectVerify = 'sequence-prospect-verify',
  UniboxScheduleCanceled = 'unibox-schedule-canceled',
  SequenceProspectBulkAction = 'sequence-prospect-bulk-action',
  ProspectBulkAction = 'prospect-bulk-action',
  LeadRevealCompleted = 'lead-reveal-completed',
  LeadRevealCompletedNoEmailOrPhone = 'lead-reveal-completed-no-email-or-phone',
  EmailAccountPaused = 'email-account-paused',
  ProspectImportAsync = 'prospect-import-async',
  SequenceProspectImportAsync = 'sequence-prospect-import-async',
  LeadRevealCompletedAsync = 'lead-reveal-completed-async',
  LeadRevealV2EmailAsync = 'lead-reveal-v2-email-async',
  LeadRevealV2EmailPhoneAsync = 'lead-reveal-v2-email-phone-async',
  LeadRevealV2PhoneAsync = 'lead-reveal-v2-phone-async',
  LeadRevealCompletedNoEmailOrPhoneAsync = 'lead-reveal-completed-no-email-or-phone-async',
  CompanyEnrichV2Async = 'company-enrich-v2-async',
  CompanyRevealV2Async = 'company-reveal-v2-async',
  CompanyEnrichFreeV2Async = 'company-free-enrich-v2-async',
  DomainRegistrationInProgress = 'domain-registration-in-progress',
  DomainRegistrationComplete = 'domain-registration-complete',
  MailboxRegistrationComplete = 'mailbox-registration-complete',
  ActiveProspectLimitExhausted = 'active-prospect-limit-exhausted',
  SnoozeTasks = 'snooze-tasks',
  SkipTasks = 'skip-tasks',
  EmailVerifier = 'email-verifier',
  SequencePausedLowInboxPlacementRate = 'sequence-paused-low-inbox-placement-rate',
  EmailAccountPausedLowInboxPlacementRate = 'email-account-paused-low-inbox-placement-rate',
  AiColumns = 'ai-column-enrichment',
  AiColumnsFailed = 'ai-column-enrichment-failed',
  NoteMention = 'note-mention',
  LinkedInAutomationDisconnected = 'linkedin-automation-disconnected',
  LinkedInAutomationApiKeyInvalid = 'linkedin-automation-api-key-invalid',
}

export enum NotificationReason {
  EmailDisconneted = 'emailDisconnected',
  SequencePaused = 'sequencePaused',
  SequenceProspectExportForClient = 'sequenceProspectExportForClient',
  SequenceReportClient = 'sequenceReportClient',
  EmailAccountBulkConnectClient = 'emailAccountBulkConnectClient',
  UniboxConversationForClient = 'uniboxConversationForClient',
  SequenceBulkProspectClient = 'sequenceBulkProspectClient',
  ProspectVerifyReport = 'prospectVerify',
  WhitelabelDomainSet = 'whitelabelDomainSet',
  DnsImport = 'dnsImport',
  DnsExport = 'dnsExport',
  SequenceProspectImport = 'sequenceProspectImport',
  SequenceProspectVerify = 'prospectVerify',
  UniboxScheduleCanceled = 'uniboxScheduleCanceled',
  ProspectBulkAction = 'prospect-bulk-action',
  SequenceProspectBulkAction = 'sequenceProspectBulkAction',
  LeadRevealCompleted = 'leadRevealCompleted',
  LeadRevealCompletedNoEmailOrPhone = 'leadRevealCompletedNoEmailOrPhone',
  EmailAccountPaused = 'emailAccountPaused',
  ProspectImportAsync = 'prospectImportAsync',
  SequenceProspectImportAsync = 'sequenceProspectImportAsync',
  ProspectImportAsyncCompleted = 'prospectImportAsyncCompleted',
  SequenceProspectImportAsyncCompleted = 'sequenceProspectImportAsyncCompleted',
  LeadRevealCompletedAsync = 'leadRevealCompletedAsync',
  LeadRevealCompletedNoEmailOrPhoneAsync = 'leadRevealCompletedNoEmailOrPhoneAsync',
  LeadRevealCompletedAsyncProcess = 'leadRevealCompletedAsyncProcess',
  LeadRevealCompletedNoEmailOrPhoneAsyncProcess = 'leadRevealCompletedNoEmailOrPhoneAsyncProcess',
  CompanyEnrichV2Async = 'companyEnrichV2Async',
  CompanyEnrichV2AsyncProcess = 'companyEnrichV2AsyncProcess',
  CompanyRevealV2Async = 'companyRevealV2Async',
  CompanyRevealV2AsyncProcess = 'companyRevealV2AsyncProcess',
  CompanyEnrichFreeV2Async = 'companyEnrichFreeV2Async',
  CompanyEnrichFreeV2AsyncProcess = 'companyEnrichFreeV2AsyncProcess',
  DomainRegistrationInProgress = 'domainRegistrationInProgress',
  DomainRegistrationComplete = 'domainRegistrationComplete',
  MailboxRegistrationComplete = 'mailboxRegistrationComplete',
  ActiveProspectLimitExhausted = 'activeProspectLimitExhausted',
  SnoozeTasks = 'snoozeTasks',
  SkipTasks = 'skipTasks',
  EmailVerificationInProgress = 'emailVerificationInProgress',
  EmailVerificationCompleted = 'emailVerificationCompleted',
  SequencePausedLowInboxPlacementRate = 'sequencePausedLowInboxPlacementRate',
  EmailAccountPausedLowInboxPlacementRate = 'emailAccountPausedLowInboxPlacementRate',
  AiColumnsFailed = 'aiColumnsFailed',
  AiColumnsCompleted = 'aiColumnsCompleted',
  AiColumnsInProgress = 'aiColumnsInProgress',
  NoteMention = 'noteMention',
  LinkedInAutomationDisconnected = 'linkedInAutomationDisconnected',
  LinkedInAutomationApiKeyInvalid = 'linkedInAutomationApiKeyInvalid',
}

export enum NotificationAction {
  Reconnect = 'reconnect',
  Resume = 'resume',
  Download = 'download',
  Login = 'login',
  ViewDraft = 'viewDraft',
  Leads = 'leads',
  Activate = 'activate',
  ViewList = 'viewList',
  ViewLeads = 'viewLead',
  ViewCompanies = 'viewCompanies',
  ViewEmailAccounts = 'viewEmailAccounts',
  View = 'view',
  ViewReport = 'viewReport',
  viewFailed = 'viewFailed',
  ViewNote = 'viewNote',
}

export enum NotificationActionType {
  BulkOperation = 'bulk-operation',
  General = 'general',
}

export type Notifications = {
  emailAccountId?: number;
  sequenceId?: number;
  notificationType: NotificationType;
  accountId: number;
  userId: number;
  _id: string;
  log: string;
  redirectUrl: string;
  timestamp: string;
  timezone: string;
  hasRead: boolean;
  emailAccountType: number;
  reportUrl: string;
  prospectEmail?: string;
  emailThreadHashId?: string;
  uuid?: string;
  actionType?: NotificationActionType;
  onScreen: boolean;
  sequenceName?: string;
  metaData: {
    totalCount: number;
    completedCount: number;
    failedCount?: number;
    updatedCount?: number;
    insertedCount?: number;
    importFile?: {
      name?: string;
      id?: number;
    };
    templateId?: string;
    emailCredits?: number;
    phoneCredits?: number;
    totalCredits?: number;
    verifiedEmailLeads?: number;
    verifiedPhoneLeads?: number;
    reportUrl?: string;
  };
  completedAt?: DateTime;
  createdAt: DateTime;
};

export type NotificationPollingResponse = {
  shouldPoll: boolean;
  notifications: Notifications[];
};

export type CompareAuthTokenPayload = {
  authToken: string;
};

export type LogoConfig = {
  primary: string;
  secondary: string;
  tertiary: string;
  favicon?: string;
  showAgencyNameWithLogo: boolean;
};

export type AgencyConfig = {
  showColdEmailTips: boolean;
  showAddStepButton: boolean;
  showEmailContentGuideHelp: boolean;
  showFAQ: boolean;
  showYTVideoGuide: boolean;
  showInfoTips: boolean;
  showGettingStarted: boolean;
  showHelp: boolean;
  showFeatureRequest: boolean;
  showChatSupport: boolean;
  showScheduleADemo: boolean;
  showWhatsNew: boolean;
  showInAppNotification: boolean;
  showClientName: boolean;
  closeOnLogout: boolean;
  isDefault: boolean;
  showNameFieldsInInviteModal: boolean;
  colorPalette: Record<string, string>;
  logo: LogoConfig;
  agencyName: string;
  agencyId?: number;
  clientName: string;
  showTermOfServiceAndPrivacyPolicy: boolean;
  ltdExpiryDate: string;
  clientBaseUrl?: string;
  clientLogo?: string;
  icon?: string;
  favicon?: string;
  companyName?: string;
  isLocallyCached?: boolean;
};

type FeatureLimit = {
  ['EMAIL.SEND']: string;
  ['PROSPECT.ADD']: string;
  ['LEAD.REVEAL']: string;
  ['SEQUENCE.EMAIL_ACCOUNT']?: number;
  ['AI_VARIANT.CREDIT']: string;
  ['INBOX_ANALYZER.TESTS']?: string;
  ['INBOX_ANALYZER.TRIGGER']?: string;
  ['AI_CREDITS']?: string;
};

type UserFeatureEngagement = {
  code: FeatureCode;
  status: UserFeatureEngagementStatus;
};

export type GetPostLoadMetaResponse = {
  featureLimits: FeatureLimit;
  accountUsageQuotaRemaining: FeatureLimit;
  accountUsageQuotaResetDate: Date;
  celloAuthToken: string;
  leadFinderAccountUsageQuotaResetDate: Date;
  totalTestsCreated?: number; // for inbox radar
  userFeatureEngagement?: UserFeatureEngagement[];
  leadFinder?: {
    hasLeadFinderV2Access?: boolean;
    version?: 'v1' | 'v2' | null;
    isVersionLocked?: boolean;
    credits?: {
      email?: number;
      emailphone?: number;
      company?: number;
    };
  };
};

export type UnreadEmailThreadsCountPayload = {
  search?: string;
  categoryId?: number;
  sequenceIds?: number[];
  emailAccountIds?: number[];
  clientIds?: number[];
  owners?: number[];
  date?: {
    startDate?: DateTime | string;
    endDate?: DateTime | string;
  };
  shouldCalculateFilterCount?: boolean;
};

export type UpdateUserFeatureEngagementPayload = {
  featureCode: FeatureCode;
  status: UserFeatureEngagementStatus;
  metadata?: InterestFormPayloadItem[];
};
